import { useState } from "react";
import { type DisplayCombatant, type EncounterState, projectForDisplay } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import type { UseRoster } from "../../roster/useRemoteRoster";
import type { ActionDraft } from "../combatInspector";
import type { AutoplayBotControls } from "../autoplayBot";
import { useCompendiumContext } from "../../CompendiumProvider";
// Type-only — the REAL exported contract from DICE.app.
import type { PreselectBonus } from "../../../routes/home/DiceApp";
import { MechInspectorBody } from "./MechInspectorBody";
import { NpcInspectorBody } from "./NpcInspectorBody";
import { PilotInspectorBody } from "./PilotInspectorBody";
import { PlayerNpcInspectorBody } from "./PlayerNpcInspectorBody";
import { assertUnmasked, mountsNpcControl, receivesUnmasked } from "./masking";
import { unitSheetIdentity } from "./UnitSheet";
import { UnitGmStrip } from "./GmStrip";
import type { ControlPanelSlot } from "./ControlPanel";
import { ObjectInspectorBody } from "./ObjectInspectorBody";

/**
 * The **PLAY** inspector body. A mech, an NPC and a pilot get a unit sheet ({@link UnitSheet}),
 * with the GM strip ({@link UnitGmStrip}) at its foot for the GM. A drone, deployable or object
 * gets {@link ObjectInspectorBody}, the same sheet look cut to what such a unit can do. Bound to a
 * **specific combatant** (the per-unit inspector double-click opened, point 4), not
 * the selection. A null combatant (the unit was just deleted, before the window auto-closes) shows a
 * brief placeholder. The EDIT desktop's inspector is {@link EditInspectorBody} instead.
 */
export function InspectorBody({
  combatant: c,
  state,
  actorId,
  encounter,
  roster,
  aim,
  setAim,
  onSelfActivate,
  fireAt,
  onDeclineOverwatch,
  onError,
  error,
  canControl,
  canEditBase,
  canConceal,
  isGm,
  bot,
  onOpenDice,
  slots,
}: {
  /**
   * The branded {@link DisplayCombatant} — the
   * host must pass the caller's combatant through `projectForDisplay` first, mirroring what
   * `projectCombatantFor` used to do at the wire boundary; a non-GM viewer's inspector may
   * therefore still render a masked or HP-hidden hostile, so this body (and its
   * `NpcInspectorBody` delegate) must discriminate before reading stat/HP fields. The brand makes
   * passing an unprojected combatant here a compile error rather than a silent stat leak.
   */
  combatant: DisplayCombatant | null;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  roster: UseRoster;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  /** Fire an activation with no spatial target (an undeclared `choose` raises the prompt modal). */
  onSelfActivate: (actionId: string) => void;
  /**
   * Fire the armed action at a target id — the host's `fireAt` (`CombatApp.tsx`). Normally the
   * canvas click-to-target gesture drives this, but the `SkirmishChooser`'s Overwatch-take FIRE
   * button calls it directly when the aim carries a `fixedTargetId`,
   * so the watcher never re-picks the mover the checkpoint already fixed.
   */
  fireAt: (targetId: string) => void;
  /** The Overwatch take's explicit decline (the {@link SkirmishChooser}'s ×): the only local gesture that answers the checkpoint on the server. */
  onDeclineOverwatch?: () => void;
  onError: (msg: string | null) => void;
  error: string | null;
  /**
   * Whether the viewer may COMMAND this unit (the affordance mirror of the server ownership gate). When false the inspector is **read-only**: tracks display but
   * don't dispatch, status editing + the Skirmish/Activate action bar are hidden. The server is still
   * the wall; this just stops a player from arming an action on a unit it doesn't own. GM ⇒ always true.
   */
  canControl: boolean;
  /**
   * Whether the viewer may author this unit's *base* mid-fight (the GM-only `edit_combatant_base` /
   * equip-mid-fight surface). GM ⇒ true; a player never sees the base-edit panel (authoring is GM
   * territory at the gate). Distinct from {@link canControl} (a player controls its own units' PLAY
   * actions, but never edits their base).
   */
  canEditBase: boolean;
  /**
   * Whether the viewer may toggle this unit's fog-of-war concealment (`set_concealed`, GM-only at
   * the server gate). GM ⇒ true; a player never sees the conceal toggle,
   * even on a unit it controls (concealment is a GM projection tool, not a player action).
   */
  canConceal: boolean;
  /**
   * Whether the viewer is the GM, plainly — the gate for chrome that is GM-territory without being
   * one *capability* the way {@link canEditBase}/{@link canConceal} each are (those two are the
   * narrower, per-affordance spellings of the same `isGm` the host computes). It mounts the unit
   * sheets' GM strip ({@link UnitGmStrip}) and the generic body's CONTROL band. The `kind === "npc"`
   * branch also reads it, alongside `canControl`, to route between the GM's sheet and the player's
   * masked card — a redundant-but-explicit `isGm ||`, since `canControl` already folds in GM.
   */
  isGm: boolean;
  /** The lifted, one-per-client autoplay bot (`./autoplayBot.ts`), for the GM strip's MORE tray. */
  bot: AutoplayBotControls;
  /**
   * Open the DICE rail page pre-filled — the host's `useCombatDice()`
   * (`routes/home/combatApp/CombatDice.tsx`). Handed on to the mech, NPC and on-foot pilot
   * bodies, whose stat grids and HASE rows carry the roll buttons (`combat-sheet-completeness` step
   * 3.2); the player's masked NPC takes nothing.
   *
   * Required-nullable — `null` is the explicit "no dice window here" and renders no roll buttons.
   */
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
  /** The game's slots: the CONTROL band's owner picker, and the player's name on a unit sheet's header. */
  slots: ControlPanelSlot[];
}) {
  // The compendium (the base-edit weapon source) comes from the app-wide provider —
  // no longer drilled through CombatApp. Called before the early return to keep hook order stable.
  // `overlay`/`loadingOverlay`: the merged action overlay a granted
  // row's `resolvable` must be derived against — see `compendium.ts`'s `CompendiumState` doc.
  const { compendium, overlay, loadingOverlay } = useCompendiumContext();
  // AS PLAYERS SEE IT (the GM's REVEAL tray): the NPC's sheet body swaps to the masked card a player
  // with no slot would get. Held here, above both bodies, so the toggle survives the swap.
  const [preview, setPreview] = useState(false);
  if (!c) {
    return <div className="gms-content gms-content-message">This unit is no longer in the encounter.</div>;
  }

  // A mech, an NPC and a pilot each get a unit sheet (`UnitSheet`), filled by the body for its kind;
  // deployable / object keep the generic body below. Branched after the hooks + null-guard so hook
  // order stays stable.
  const identity = unitSheetIdentity({ combatant: c, compendium, pilots: roster.state?.pilots ?? {}, slots });
  // The unit sheets' GM strip; the generic body below doesn't mount it. The GM's own projection is
  // identity (`projectCombatantFor`'s `role === "gm"` return), so `assertUnmasked` is a backstop,
  // never a cast.
  const gm =
    isGm ? (
      <UnitGmStrip
        combatant={assertUnmasked(c)}
        state={state}
        actorId={actorId}
        encounter={encounter}
        slots={slots}
        compendium={compendium}
        setAim={setAim}
        bot={bot}
        preview={preview}
        onPreview={setPreview}
      />
    ) : undefined;
  if (c.kind === "npc") {
    // AS PLAYERS SEE IT: the masked card from the SAME projection a player gets, for a player with
    // no slot and no grants. Display only: the card dispatches nothing (`granted: null`), and the GM
    // strip stays under it.
    if (isGm && preview) {
      const asPlayers = projectForDisplay({ combatant: assertUnmasked(c), viewerSlot: null, role: "player", state });
      return (
        <PlayerNpcInspectorBody
          combatant={asPlayers}
          identity={identity}
          state={state}
          compendium={compendium}
          granted={null}
          band={<div className="unit-sheet-preview-band">AS PLAYERS SEE IT · A PLAYER WITH NO GRANTS</div>}
          gm={gm}
        />
      );
    }
    // A player's NPC inspector is a distinct read-only DOSSIER, not a stripped-down control panel.
    // Routed on the projection's shape as well as control (`mountsNpcControl`): the GM, and a player
    // who controls this NPC AND receives it unmasked (an owned unit, or CTRL + EYES grants), get the
    // control panel. `frontend/e2e/unit-ai-advisor.e2e.ts` seeds a player-controlled NPC and
    // asserts the control surface. A player holding CTRL without EYES gets the dossier with the
    // granted turn strip on its state line (`decisions/0179`: control without visibility cannot fire). Reads the SAME
    // `DisplayCombatant` the host already minted — no second projection call.
    if (mountsNpcControl(c, { isGm, canControl })) {
      return (
        <NpcInspectorBody
          combatant={assertUnmasked(c)}
          identity={identity}
          state={state}
          actorId={actorId}
          encounter={encounter}
          aim={aim}
          setAim={setAim}
          onSelfActivate={onSelfActivate}
          fireAt={fireAt}
          onDeclineOverwatch={onDeclineOverwatch}
          canControl={canControl}
          gm={gm}
          error={error}
          compendium={compendium}
          overlay={overlay}
          loadingOverlay={loadingOverlay}
          onOpenDice={onOpenDice}
        />
      );
    }
    return (
      <PlayerNpcInspectorBody
        combatant={c}
        identity={identity}
        state={state}
        compendium={compendium}
        granted={canControl ? { actorId, encounter } : null}
      />
    );
  }
  // A mech or pilot spawns pc-side, but `set_controller`'s `allegiance` (the CLI's `--side npc`)
  // can move one to the npc side, where a player without the view grant receives it masked or
  // HP-hidden like any hostile NPC: the same dossier, with the granted turn strip for a CTRL
  // holder. The GM's projection is identity, so this never catches the GM.
  if ((c.kind === "mech" || c.kind === "pilot") && !receivesUnmasked(c)) {
    return (
      <PlayerNpcInspectorBody
        combatant={c}
        identity={identity}
        state={state}
        compendium={compendium}
        granted={canControl ? { actorId, encounter } : null}
      />
    );
  }
  if (c.kind === "mech") {
    return (
      <MechInspectorBody
        combatant={assertUnmasked(c)}
        identity={identity}
        state={state}
        actorId={actorId}
        encounter={encounter}
        aim={aim}
        setAim={setAim}
        fireAt={fireAt}
        onDeclineOverwatch={onDeclineOverwatch}
        canControl={canControl}
        gm={gm}
        error={error}
        compendium={compendium}
        overlay={overlay}
        loadingOverlay={loadingOverlay}
        onOpenDice={onOpenDice}
      />
    );
  }
  if (c.kind === "pilot") {
    return (
      <PilotInspectorBody
        combatant={assertUnmasked(c)}
        gearIds={(c.sourceRef ? roster.state?.pilots[c.sourceRef]?.gear : undefined) ?? []}
        identity={identity}
        state={state}
        actorId={actorId}
        encounter={encounter}
        aim={aim}
        setAim={setAim}
        fireAt={fireAt}
        onDeclineOverwatch={onDeclineOverwatch}
        canControl={canControl}
        gm={gm}
        error={error}
        compendium={compendium}
        overlay={overlay}
        loadingOverlay={loadingOverlay}
        onOpenDice={onOpenDice}
      />
    );
  }

  return (
    <ObjectInspectorBody
      combatant={c}
      state={state}
      actorId={actorId}
      encounter={encounter}
      roster={roster}
      compendium={compendium}
      aim={aim}
      setAim={setAim}
      onSelfActivate={onSelfActivate}
      onError={onError}
      error={error}
      canControl={canControl}
      canEditBase={canEditBase}
      canConceal={canConceal}
      isGm={isGm}
      slots={slots}
    />
  );
}
